import type { UnwrapAxiosResponse } from '@seccl/api-swr';
import type { ClientManagementApi } from '@seccl/client';
import {
  type ClientManagementApiClientFirmIdGetRequest,
  type ClientManagementApiClientSearchFirmIdGetRequest,
  type GetClientList,
  type GetClientSearch,
} from '@seccl/client';
import type { SWRResponse } from 'swr';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useFirmId } from '../useFirmId';
import { clientManagementApi } from './api';

/**
 * Hook for searching clients within a firm using a query string.
 * It integrates with the client management API to query the firm clients.
 *
 * @param {string} [query] - The search term to filter clients. Defaults to undefined.
 * @param {number} [pageSize=20] - The number of clients to fetch per page. Defaults to 20.
 * @param {Partial<ClientManagementApiClientSearchFirmIdGetRequest>} [params] - Optional additional parameters for the search request.
 * @param {boolean} [waitFor] - Determines whether to wait for data fetching to complete before rendering.
 * @param {GetClientList} [fallbackData] - Optional fallback data to be used during server-side rendering or in the absence of fetched results.
 */
export const useSearchClients = (
  query?: string,
  pageSize: number = 20,
  params?:
    | Partial<ClientManagementApiClientSearchFirmIdGetRequest>
    | Partial<ClientManagementApiClientFirmIdGetRequest>,
  waitFor?: boolean,
  fallbackData?: GetClientList
) => {
  const firmId = useFirmId();

  const { hasFeatureFlag } = useFeatureFlag();

  const useEnhancedSearch = hasFeatureFlag('seccl-25590-client-atlas-search-ui');

  if (useEnhancedSearch) {
    return clientManagementApi.clientSearchFirmIdGet.useQuery({
      cacheKey: ['firmId', 'pageSize', 'query', 'clientType', 'migrationStatus', 'migrationSuitabilityStatus'],
      params: { pageSize, query, firmId, ...params } as ClientManagementApiClientSearchFirmIdGetRequest,
      waitFor,
      swrConfig: { fallbackData: fallbackData as GetClientSearch, revalidateOnMount: true },
    });
  }

  return clientManagementApi.clientFirmIdGet.useQuery({
    cacheKey: ['firmId', 'pageSize', 'name', 'clientType', 'migrationStatus', 'migrationSuitabilityStatus'],
    params: { page: 1, pageSize, name: query, firmId, ...params } as ClientManagementApiClientFirmIdGetRequest,
    waitFor,
    swrConfig: { fallbackData: fallbackData as GetClientList, revalidateOnMount: true },
  }) as SWRResponse<UnwrapAxiosResponse<ClientManagementApi['clientFirmIdGet']>>;
};
